"use client";

/**
 * Scene0HeroCta — client island for the Scene 0 above-fold primary CTA.
 *
 * Why a separate file: Scene0Static is a Server Component (UI-53 LCP fix).
 * Adding `onClick` to the `<a>` would force the whole scene to `"use client"`
 * and undo the LCP win. By extracting the `<a>` into this tiny client island,
 * the H1 + subhead + trust signal remain server-rendered (RSC HTML in the
 * initial response), while the CTA gets its analytics handler.
 *
 * Markup parity: matches the previous inline `<a>` exactly (same classes,
 * same Lucide ArrowRight, same focus ring). Visual output unchanged.
 *
 * Analytics: fires `landing.hero_cta.click` on click. Tracking is synchronous
 * (no `await`), so the browser navigation is not delayed. PostHog + GA4
 * fan-out is non-blocking inside the wrapper.
 */

import { ArrowRight } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

interface Scene0HeroCtaProps {
  href: string;
  label: string;
}

export function Scene0HeroCta({ href, label }: Scene0HeroCtaProps) {
  const handleClick = () => {
    trackEvent("landing.hero_cta.click", { cta_label: label, href });
  };

  return (
    <a
      href={href}
      onClick={handleClick}
      className="group relative inline-flex items-center gap-2 rounded-full bg-amber-400 px-7 py-3.5 xl:px-8 xl:py-4 2xl:px-10 2xl:py-5 text-[16px] xl:text-[18px] 2xl:text-[20px] font-semibold text-bg-base shadow-[0_8px_32px_rgba(251,191,36,0.35)] transition-all hover:bg-amber-300 hover:shadow-[0_12px_40px_rgba(251,191,36,0.5)] hover:-translate-y-0.5 active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/50 focus-visible:ring-offset-2 focus-visible:ring-offset-bg-base"
    >
      {label}
      <ArrowRight
        size={18}
        strokeWidth={2.5}
        className="transition-transform group-hover:translate-x-0.5"
        aria-hidden
      />
    </a>
  );
}
